import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';

// ── Web Speech API ────────────────────────────────────────────────────────────
function speak(text) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.rate = 0.92;
  u.pitch = 1.05;
  u.volume = 1;
  const voices = window.speechSynthesis.getVoices();
  const preferred = voices.find(v => v.lang === 'en-IN') ||
                    voices.find(v => v.lang.startsWith('en') && v.localService) ||
                    voices.find(v => v.lang.startsWith('en'));
  if (preferred) u.voice = preferred;
  window.speechSynthesis.speak(u);
}

// ── Desktop notification ──────────────────────────────────────────────────────
export function sendDesktopNotification(title, body, icon = '/favicon.svg') {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'granted') {
    new Notification(title, { body, icon });
  }
}

// Request permission once per browser (called from Layout on first load)
export function requestNotificationPermission() {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

const REMINDER_LUNCH      = '14:00';
const REMINDER_WORK_LIST  = '18:45';
const REMINDER_CLOCK_OUT  = '19:00';
const REMINDER_SNOOZE_1   = '19:10';
const REMINDER_OVERTIME   = '19:15';

function hhmm(date) {
  return date.toTimeString().slice(0, 5);
}

// ── Time picker helper ────────────────────────────────────────────────────────
function OvertimeTimePicker({ onConfirm, onDecline }) {
  const [endTime, setEndTime] = useState('20:00');
  return (
    <div>
      <div style={{ fontSize: 17, fontWeight: 700, color: '#111', marginBottom: 6 }}>Do you want to work overtime?</div>
      <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>Select your planned end time:</div>
      <input
        type="time"
        value={endTime}
        min="19:16"
        max="23:59"
        onChange={e => setEndTime(e.target.value)}
        style={{ fontSize: 20, fontWeight: 700, textAlign: 'center', border: '2px solid #073E81', borderRadius: 8, padding: '8px 16px', marginBottom: 20, width: '100%', boxSizing: 'border-box' }}
      />
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={() => onConfirm(endTime)}
          style={{ background: '#073E81', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          Yes, work until {endTime}
        </button>
        <button
          onClick={onDecline}
          style={{ background: '#f5f5f5', color: '#555', border: '1px solid #e0e0e0', borderRadius: 8, padding: '10px 24px', fontSize: 14, cursor: 'pointer' }}>
          No, clock out
        </button>
      </div>
    </div>
  );
}

export default function ReminderSystem() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const firstName = user?.name?.split(' ')[0] || user?.username || 'there';

  const [popup, setPopup] = useState(null);
  const firedRef = useRef(new Set());
  const snoozedRef = useRef(false);
  const overtimeEndRef = useRef(null); // HH:MM string if user chose overtime
  const breakFiredRef = useRef(new Set());

  // ── Login greeting ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const key = `login_greeted_${user.id}_${new Date().toDateString()}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');

    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
    const msg = `${greeting} ${firstName}, please clock in`;

    setTimeout(() => {
      speak(msg);
      sendDesktopNotification('8D Business OS', msg);
      setPopup({ type: 'login', message: msg });
    }, 1500);
  }, [user]);

  // ── Time-based reminders ──────────────────────────────────────────────────
  const checkTime = useCallback(() => {
    if (!user) return;
    const now = new Date();
    const t = hhmm(now);

    const fire = (key, message, extra = {}) => {
      if (firedRef.current.has(key)) return;
      firedRef.current.add(key);
      speak(message);
      sendDesktopNotification('8D Business OS', message);
      setPopup({ type: key, message, ...extra });
    };

    if (t === REMINDER_LUNCH) {
      fire('lunch', `${firstName}, it's 2 o'clock. Time for your lunch break!`);
    }

    if (t === REMINDER_WORK_LIST) {
      fire('work_list', `${firstName}, please update your work list before you leave.`);
    }

    if (t === REMINDER_CLOCK_OUT) {
      fire('clock_out_1', `${firstName}, it's 7 o'clock. Time to clock out. Have a good evening.`, { canSnooze: true, canClockOut: true });
    }

    if (t === REMINDER_SNOOZE_1 && snoozedRef.current && !overtimeEndRef.current) {
      fire('clock_out_2', `${firstName}, don't forget to clock out.`, { canSnooze: false, canClockOut: true });
    }

    // 7:15 PM — overtime prompt (only if not already in overtime mode)
    if (t === REMINDER_OVERTIME && !overtimeEndRef.current) {
      fire('overtime_ask', '', { isOvertimeAsk: true });
    }

    // If user chose overtime and we've reached their selected end time
    if (overtimeEndRef.current && t === overtimeEndRef.current) {
      const key = `overtime_end_${overtimeEndRef.current}`;
      fire(key, `${firstName}, it's ${overtimeEndRef.current}. Are you still working?`, { canSnooze: false, canClockOut: true, canOvertimeSnooze: true });
    }
  }, [user, firstName]);

  useEffect(() => {
    const id = setInterval(checkTime, 15000);
    checkTime();
    return () => clearInterval(id);
  }, [checkTime]);

  // ── Break-resume reminder ────────────────────────────────────────────────
  // A break that's never explicitly resumed stays "open" until clock-out, at which
  // point ALL of that elapsed time gets subtracted as break — silently shrinking a
  // full workday down to a half-day. Nudge the user well before that becomes a problem.
  const checkActiveBreak = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/attendance-sessions/today');
      const ab = data?.activeBreak;
      if (!ab) return;
      const elapsedMin = (Date.now() - new Date(ab.started_at).getTime()) / 60000;
      const fire = (thresholdKey, minutesLabel) => {
        const key = `break_${ab.id}_${thresholdKey}`;
        if (breakFiredRef.current.has(key)) return;
        breakFiredRef.current.add(key);
        const msg = `${firstName}, you've been on break for over ${minutesLabel}. Don't forget to resume work in the app!`;
        speak(msg);
        sendDesktopNotification('⏱ Still on break?', msg);
        setPopup({ type: 'break_resume', message: msg, canResumeBreak: true });
      };
      if (elapsedMin >= 90) fire('90', '1.5 hours');
      else if (elapsedMin >= 45) fire('45', '45 minutes');
    } catch { /* silent */ }
  }, [user, firstName]);

  useEffect(() => {
    const id = setInterval(checkActiveBreak, 60000);
    checkActiveBreak();
    return () => clearInterval(id);
  }, [checkActiveBreak]);

  async function handleResumeBreak() {
    try {
      await api.post('/attendance-sessions/break/end');
      speak(`Welcome back, ${firstName}!`);
      setPopup(null);
    } catch {
      setPopup(null);
    }
  }

  // ── Clock out helper ──────────────────────────────────────────────────────
  async function handleClockOut() {
    try {
      await api.post('/attendance-sessions/clock-out');
      // Invalidate attendance query so Clock In/Out page updates instantly
      qc.invalidateQueries(['attendance-today']);
      qc.invalidateQueries(['team-today']);
      speak(`Clocked out. Have a great evening, ${firstName}!`);
      sendDesktopNotification('Clocked Out', `Have a great evening, ${firstName}!`);
      setPopup({ type: 'clocked_out', message: `Clocked out successfully. Have a great evening, ${firstName}!` });
      overtimeEndRef.current = null;
    } catch (e) {
      const msg = e.response?.data?.error || 'Could not clock out — please do it manually.';
      setPopup({ type: 'error', message: msg });
    }
  }

  async function handleOvertimeConfirm(endTime) {
    overtimeEndRef.current = endTime;
    // Notify owner about overtime
    try {
      await api.post('/attendance-sessions/overtime-request', { planned_end: endTime });
    } catch { /* non-fatal */ }
    speak(`Overtime noted. You'll be reminded at ${endTime}.`);
    setPopup(null);
  }

  function handleOvertimeDecline() {
    overtimeEndRef.current = null;
    handleClockOut();
  }

  if (!popup) return null;

  const isClockOut = popup.type.startsWith('clock_out') || popup.type.startsWith('overtime_end');
  const bgColor = isClockOut ? '#073E81'
    : popup.type === 'work_list' ? '#b45309'
    : popup.type === 'lunch' ? '#b45309'
    : popup.type === 'overtime_ask' ? '#7C3AED'
    : popup.type === 'break_resume' ? '#D97706'
    : '#1a6b3c';
  const icon = isClockOut ? '🕖'
    : popup.type === 'work_list' ? '📋'
    : popup.type === 'lunch' ? '🍽️'
    : popup.type === 'overtime_ask' ? '⏰'
    : popup.type === 'break_resume' ? '⏱️'
    : '👋';

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.45)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: '32px 36px',
        maxWidth: 420, width: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        textAlign: 'center', animation: 'popIn 0.25s ease',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: bgColor, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 28, margin: '0 auto 18px',
        }}>{icon}</div>

        {popup.isOvertimeAsk ? (
          <OvertimeTimePicker onConfirm={handleOvertimeConfirm} onDecline={handleOvertimeDecline} />
        ) : (
          <>
            <div style={{ fontSize: 17, fontWeight: 600, color: '#111', marginBottom: 8, lineHeight: 1.5 }}>
              {popup.message}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 22, flexWrap: 'wrap' }}>
              {popup.canResumeBreak && (
                <button
                  onClick={handleResumeBreak}
                  style={{ background: '#D97706', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  ▶ Resume Work Now
                </button>
              )}
              {popup.canClockOut && (
                <button
                  onClick={handleClockOut}
                  style={{ background: '#073E81', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  ■ Clock Out Now
                </button>
              )}
              {popup.canSnooze && (
                <button
                  onClick={() => { snoozedRef.current = true; setPopup(null); }}
                  style={{ background: '#f0f4ff', color: '#073E81', border: '1px solid #c5d5f5', borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  Snooze 10 min
                </button>
              )}
              {popup.canOvertimeSnooze && (
                <button
                  onClick={() => {
                    const [h, m] = overtimeEndRef.current.split(':').map(Number);
                    const next = new Date();
                    next.setHours(h, m + 30, 0, 0);
                    overtimeEndRef.current = next.toTimeString().slice(0, 5);
                    setPopup(null);
                  }}
                  style={{ background: '#f0f4ff', color: '#073E81', border: '1px solid #c5d5f5', borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  Snooze 30 min
                </button>
              )}
              <button
                onClick={() => setPopup(null)}
                style={{ background: '#f5f5f5', color: '#555', border: '1px solid #e0e0e0', borderRadius: 8, padding: '10px 22px', fontSize: 14, cursor: 'pointer' }}>
                Dismiss
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes popIn {
          from { transform: scale(0.85); opacity: 0; }
          to   { transform: scale(1);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
